// UserData.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { avatarBg } from '../utils/avatars';
import Avatar from '../Components/Avatar/Avatar';
import './CSS/Dashboard.css'; // shared .dashboard-subtabs tab strip

const STATUS_COLORS = {
  completed: '#48bb78',
  pending: '#ed8936',
  failed: '#f56565',
};

const UserData = ({ userData, setUserData, backendUrl, resetKey }) => {
  const navigate = useNavigate();
  const [userProfileData, setUserProfileData] = useState({
    name: userData?.name || '',
    email: userData?.email || '',
    password: '',
  });
  const [myProducts, setMyProducts] = useState([]);
  const [myPurchases, setMyPurchases] = useState([]);
  // Full user doc fetched here (includes date/isVerified/_id). The `userData`
  // prop comes from useAuth and may lack those fields, and setUserData is a
  // no-op in the Dashboard, so we keep our own copy for the Account section.
  const [fullUser, setFullUser] = useState(null);
  const [activeSection, setActiveSection] = useState('profile'); // profile | content | orders

  // Fetch user data again to ensure updated data is displayed
  const fetchUserData = async () => {
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/user/getuserdata`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
      });
      const result = await response.json();
      if (result.success) {
        setUserData(result.user);
        setFullUser(result.user);
        setUserProfileData({
          name: result.user.name,
          email: result.user.email,
          password: '',
        });
      } else {
        console.error('Error fetching user data:', result.message);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  // Fetch the user's content (products with access) and order history.
  const fetchMyContent = async () => {
    const token = localStorage.getItem('auth-token');
    try {
      const [productsRes, ordersRes] = await Promise.all([
        fetch(`${backendUrl}/permissions/my-products`, { headers: { 'auth-token': token } }),
        fetch(`${backendUrl}/purchases/mine`, { headers: { 'auth-token': token } }),
      ]);
      const productsData = await productsRes.json();
      const ordersData = await ordersRes.json();
      if (productsData.success) setMyProducts(productsData.products || []);
      if (ordersData.success) setMyPurchases(ordersData.purchases || []);
    } catch (error) {
      console.error('Error fetching content/orders:', error);
    }
  };

  // Fetch user data when component loads
  useEffect(() => {
    fetchUserData();
    // eslint-disable-next-line
  }, []);

  // (Re)fetch content + orders on mount and whenever the tab is reselected.
  useEffect(() => {
    fetchMyContent();
    // eslint-disable-next-line
  }, [resetKey]);

  // Repopulate the profile form when the tab is (re)selected. NOTE: we depend on
  // resetKey only — NOT on the userData prop — otherwise a background re-fetch
  // that changes the prop's object identity would wipe what the user is typing.
  useEffect(() => {
    const src = fullUser || userData;
    if (src) {
      setUserProfileData({
        name: src.name || '',
        email: src.email || '',
        password: '',
      });
    }
    // eslint-disable-next-line
  }, [resetKey]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserProfileData({ ...userProfileData, [name]: value });
  };

  const updateProfile = async () => {
    const token = localStorage.getItem('auth-token');
    const current = fullUser || userData || {};
    const dataToUpdate = {};

    if (userProfileData.name.trim() && userProfileData.name.trim() !== current.name) {
      dataToUpdate.name = userProfileData.name.trim();
    }
    if (userProfileData.email.trim() && userProfileData.email.trim() !== current.email) {
      dataToUpdate.email = userProfileData.email.trim();
    }
    if (userProfileData.password) {
      dataToUpdate.password = userProfileData.password;
    }

    if (Object.keys(dataToUpdate).length === 0) { // No changes made
      alert('No changes detected.');
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/user/updateloggeduserdata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
        body: JSON.stringify(dataToUpdate),
      });

      const result = await response.json();
      if (result.success) {
        alert('Profile updated successfully!');
        await fetchUserData(); // Refresh our own copy
        setUserProfileData((prev) => ({ ...prev, password: '' }));
        window.dispatchEvent(new Event('storage')); // refresh navbar/name immediately
      } else {
        alert('Error updating profile: ' + result.message);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('An error occurred while updating the profile.');
    }
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

  const colorInputRef = useRef(null);
  const openColorPicker = () => colorInputRef.current && colorInputRef.current.click();

  // Save the chosen avatar color (or '' to reset to the auto color) immediately.
  const saveAvatar = async (id) => {
    const token = localStorage.getItem('auth-token');
    try {
      const res = await fetch(`${backendUrl}/user/updateloggeduserdata`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'auth-token': token },
        body: JSON.stringify({ avatar: id }),
      });
      const data = await res.json();
      if (data.success) {
        setFullUser((prev) => ({ ...(prev || {}), avatar: id }));
        window.dispatchEvent(new Event('storage')); // refresh navbar avatar immediately
      } else {
        alert('Could not update avatar: ' + (data.message || ''));
      }
    } catch (e) {
      console.error('Error updating avatar:', e);
    }
  };

  // Download the proof-of-purchase PDF. The endpoint needs the auth-token
  // header, so we fetch a blob and trigger the download manually.
  const downloadReceipt = async (purchaseId, ref) => {
    const token = localStorage.getItem('auth-token');
    try {
      const res = await fetch(`${backendUrl}/purchases/${purchaseId}/receipt`, {
        headers: { 'auth-token': token },
      });
      const data = await res.json();
      if (!res.ok || !data.success || !data.pdfBase64) {
        alert('Could not download the receipt.');
        return;
      }
      // Rebuild the PDF file from the base64 payload.
      const bytes = Uint8Array.from(atob(data.pdfBase64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename || `receipt-${ref || purchaseId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error downloading receipt:', e);
      alert('Could not download the receipt.');
    }
  };

  // Account data: prefer the full doc we fetched (has date/isVerified/_id);
  // fall back to the prop from useAuth.
  const account = fullUser || userData;

  // "Member since": use the stored `date`, or fall back to the creation time
  // embedded in the account's Mongo ObjectId (for legacy users created before
  // the `date` field existed, so it never shows "—").
  const memberSince = (() => {
    if (account?.date) return account.date;
    const id = account?._id;
    if (typeof id === 'string' && /^[a-f0-9]{24}$/i.test(id)) {
      return new Date(parseInt(id.substring(0, 8), 16) * 1000);
    }
    return null;
  })();

  const SUB_TABS = [
    { key: 'profile', label: 'Profile' },
    { key: 'content', label: 'My Content' },
    { key: 'orders', label: 'My Orders' },
  ];

  return (
    <div>
      {/* Sub-navigation to avoid one long scroll. Shares the Dashboard tab strip
          styling so every tab row in the app looks the same. */}
      <div className="dashboard-subtabs">
        {SUB_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`dashboard-subtab ${activeSection === tab.key ? 'active' : ''}`}
            onClick={() => setActiveSection(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeSection === 'profile' && (
      <>
      <div className="form-group" style={{ maxWidth: '600px' }}>
        <h2>User Data</h2>

        {/* Avatar — click it to change its color */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
          <div style={{ textAlign: 'center' }}>
            <Avatar
              user={account}
              size={72}
              onClick={openColorPicker}
              style={{ border: '2px solid #2a2a33' }}
            />
            {/* Hidden native color input, triggered by clicking the avatar. */}
            <input
              ref={colorInputRef}
              type="color"
              value={avatarBg(account)}
              onChange={(e) => saveAvatar(e.target.value)}
              tabIndex={-1}
              aria-hidden="true"
              style={{ position: 'absolute', width: 0, height: 0, opacity: 0, border: 0, padding: 0, margin: 0 }}
            />
            <div style={{ fontSize: 11, color: '#a0aec0', marginTop: 6 }}>
              <span style={{ cursor: 'pointer' }} onClick={openColorPicker}>Click to change color</span>
              {account?.avatar && (
                <>
                  {' · '}
                  <span style={{ cursor: 'pointer' }} onClick={() => saveAvatar('')}>reset</span>
                </>
              )}
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 600 }}>{account?.name || ''}</div>
            <div style={{ color: '#a0aec0', fontSize: '13px' }}>{account?.email || ''}</div>
          </div>
        </div>

        <label htmlFor="name">Name</label>
        <input
          type="text"
          name="name"
          value={userProfileData.name}
          onChange={handleInputChange}
          placeholder="Name"
        />
        <label htmlFor="email">Email</label>
        <input
          type="email"
          name="email"
          value={userProfileData.email}
          onChange={handleInputChange}
          placeholder="Email"
        />
        <label htmlFor="password">Change Password</label>
        <input
          type="password"
          name="password"
          value={userProfileData.password}
          onChange={handleInputChange}
          placeholder="New Password"
        />
        <button onClick={updateProfile}>Update Profile</button>
      </div>

      {/* Account details */}
      <div className="form-group" style={{ maxWidth: '600px' }}>
        <h2>Account</h2>
        <p style={{ margin: '4px 0' }}>Member since: {formatDate(memberSince)}</p>
        <p style={{ margin: '4px 0' }}>
          Email:{' '}
          <span style={{ color: account?.isVerified ? '#48bb78' : '#f56565' }}>
            {account?.isVerified ? '✓ Verified' : 'Not verified'}
          </span>
        </p>
      </div>
      </>
      )}

      {/* Products the user can access */}
      {activeSection === 'content' && (
      <div className="form-group" style={{ maxWidth: '600px' }}>
        <h2>My Content</h2>
        {myProducts.length === 0 ? (
          <p style={{ color: '#a0aec0' }}>You don't have access to any content yet.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {myProducts.map((p) => (
              <li
                key={p._id}
                onClick={() => navigate(`/product/${p._id}`)}
                style={{
                  padding: '10px 12px',
                  marginBottom: '8px',
                  background: '#15151a',
                  border: '1px solid #2a2a33',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>{p.title}</span>
                <span style={{ color: '#9a51e2', fontSize: '13px' }}>▶ Play</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      )}

      {/* Purchase / order history */}
      {activeSection === 'orders' && (
      <div className="form-group" style={{ maxWidth: '600px' }}>
        <h2>Order History</h2>
        {myPurchases.length === 0 ? (
          <p style={{ color: '#a0aec0' }}>No purchases yet.</p>
        ) : (
          <div style={{ overflowX: 'auto', width: '100%' }}>
          <table style={{ width: '100%', minWidth: '480px', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#a0aec0' }}>
                <th style={{ padding: '6px' }}>Date</th>
                <th style={{ padding: '6px' }}>Product</th>
                <th style={{ padding: '6px' }}>Amount</th>
                <th style={{ padding: '6px' }}>Status</th>
                <th style={{ padding: '6px' }}>Order</th>
                <th style={{ padding: '6px' }}>Receipt</th>
              </tr>
            </thead>
            <tbody>
              {myPurchases.map((o) => (
                <tr key={o._id} style={{ borderTop: '1px solid #2a2a33' }}>
                  <td style={{ padding: '6px' }}>{formatDate(o.purchasedAt)}</td>
                  <td style={{ padding: '6px' }}>{o.productId?.title || '—'}</td>
                  <td style={{ padding: '6px' }}>{o.price != null ? `${o.price} €` : '—'}</td>
                  <td style={{ padding: '6px' }}>
                    <span style={{ color: STATUS_COLORS[o.status] || '#a0aec0', fontWeight: 600 }}>
                      {o.status}
                    </span>
                  </td>
                  <td style={{ padding: '6px', color: '#a0aec0' }}>{o.orderNumber || '—'}</td>
                  <td style={{ padding: '6px' }}>
                    {o.status === 'completed' ? (
                      <button
                        onClick={() => downloadReceipt(o._id, o.orderNumber)}
                        style={{
                          background: 'transparent',
                          border: '1px solid #9a51e2',
                          color: '#9a51e2',
                          borderRadius: '6px',
                          padding: '4px 10px',
                          cursor: 'pointer',
                          fontSize: '13px',
                        }}
                      >
                        PDF
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default UserData;
